import * as Models from "./rlAdmin.models";
import * as Consts from "./rlAdmin.consts";
import * as URI from "urijs";
import * as moment from "moment";

/**
 * Static security utility functions
 **/
export class SecurityUtil {
	/**
	  * Get the right status of a rightFlag on a single permissionValue
	  * @param permissionValue the permissions value of a single security node
	  * @param rightFlag the rights flag you want to evaluate  (you may logically OR multiple rightFlag values with |)
	  * @param matchAll for matching multiple combined rightFlags, do you want to match all flags or match any flags?
	  * @return 0 - neutral, 1 - allow, -1 - deny
	  **/
    static GetRightStatus(permissionValue: number, rightFlag: number, matchAll: boolean = true) {

        // bitshift the flag by 16 to get the deny value
        var denyFlag = rightFlag << 16;
        var deny = false;
        if (matchAll) {
            // logical AND must match the denyFlag exactly (for multiple flags)
            deny = (permissionValue & denyFlag) == denyFlag;
        } else {
            // logical AND must match any denyFlag (for multiple flags)
            deny = (permissionValue & denyFlag) > 0;
        }
        // a positive for Deny status overrides any allows so quit here
        if (deny) {
            return Consts.AccessResult.Deny;
        }

        var allow = true;
        if (matchAll) {
            // logical AND must match the rightFlag exactly (for multiple flags)
            allow = (permissionValue & rightFlag) == rightFlag;
        } else {
            // logical AND must match any rightFlag (for multiple flags)
            allow = (permissionValue & rightFlag) > 0;
        }

        // return allow if matched
        if (allow) {
            return Consts.AccessResult.Allow;
        }

        // return neutral if nothing matched
        return Consts.AccessResult.NotSet;
    }

	/**
	 * Evaluates a permission key against a permission set, rolling heirarchically upwards to find a result
	 * 
	 * @param permissionSet The set of all heirarchical permissions
	 * @param permissionKey The pipe delimeted key representing a node in the heirarchy
	 * @param rightFlag Bit flag representing the right you wish to evaluate
	 * @return 0 - neutral, 1 - allow, -1 - deny
	 **/
    static GetEffectivePermission(permissionSet: Models.IPermissionSet, permissionKey: string, rightFlag: number) {

        var permission = Consts.AccessResult.NotSet;

        // get ascending key list from permissionKey
        var ascendingKeys = SecurityUtil.GetAscendingKeys(permissionKey);

        // for each ascending node key
        _.forEach(ascendingKeys, (key) => {
            // get this node's permissionValue from 
            var permissionValue = permissionSet[key];
            // if a value exists
            if (permissionValue != null) {
                // evaluate at this level
                var rightStatus = SecurityUtil.GetRightStatus(permissionValue, rightFlag);
                switch (rightStatus) {
                    // allows can override neutrals
                    case Consts.AccessResult.Allow:
                        if (permission == Consts.AccessResult.NotSet) {
                            permission = Consts.AccessResult.Allow;
                        }
                        break;
                    // denies can override allow & neutral
                    case Consts.AccessResult.Deny:
                        permission = Consts.AccessResult.Deny;
                        break;
                }
                // if deny is found, exit method early
                if (permissionValue == Consts.AccessResult.Deny) {
                    return false; // exits the _.forEach
                }
            }
        });
        return permission;
    }

	/**
	 * Turns a pipe delimited security key into an array of keys ascending towards the root node
	 * @param permissionKey the pipe separated key
	 * @return an array of keys starting with input permissionKey and ending at the root security node key
	 **/
    static GetAscendingKeys(permissionKey: string) {

        // split out keys by |
        var splits = permissionKey.split("|");
        var output: string[] = [];

        while (splits.length > 0) {
            // join split keys and add to output
            output.push(splits.join("|"));
            // remove last key from splits
            splits.pop();
        }

        return output;
    }

	/**
	 * Update a permission value with the changed rightFlag of your choice
	 * @param permissionValue: current elements permission number
	 * @param rightFlag the number flag representing the right you wish to change
	 * @param status the direction you want to set this right on this permission 1 - allow, 0 - neutral, -1 - deny
	 * @return the updated permissionValue
	 **/
    static SetRightStatus(permissionValue: number, rightFlag: number, status: number) {

        var denyRightFlag = rightFlag << 16;

        if (status == 1) {
            // remove deny
            permissionValue = permissionValue & ~(denyRightFlag);
            // add allow
            permissionValue = permissionValue | rightFlag;
        }
        else if (status == -1) {
            // remove allow
            permissionValue = permissionValue & ~(rightFlag);
            // add deny
            permissionValue = permissionValue | (denyRightFlag);
        }
        else if (status == 0) {
            // remove allow
            permissionValue = permissionValue & ~(rightFlag);
            // remove deny
            permissionValue = permissionValue & ~(denyRightFlag);
        }

        return permissionValue;
    }
}


export class AclUtil {

    static getAclUrl(charTypeID: number): string {
        switch (charTypeID) {
            case Consts.CharTypeIDs.property: return Consts.Acl.Activities._Site.Mod1;
            case Consts.CharTypeIDs.user: return Consts.Acl.Activities._Site.Mod2;
            case Consts.CharTypeIDs.transaction: return Consts.Acl.Activities._Site.Mod4;
            case Consts.CharTypeIDs.project: return Consts.Acl.Activities._Site.Mod16;
            case Consts.CharTypeIDs.invoice: return Consts.Acl.Activities._Site.Mod7;
            case Consts.CharTypeIDs.inventory: return Consts.Acl.Activities._Site.Mod17;
            case Consts.CharTypeIDs.document: return Consts.Acl.Activities._Site.Mod14;
            case Consts.CharTypeIDs.job: return Consts.Acl.Activities._Site.Mod15;
        }
    }

    static getDataEntityAcl(charTypeID: number = null, templateGroupID: number = null, templateID: number = null, charGroupID: number = null, charID: number = null): string {
        var output = Consts.Acl.Data.entities;
        if (charTypeID != null) {
            output += `/ct_${charTypeID}`;
            if (templateGroupID != null) {
                output += `/tg_${templateGroupID}`;
                if (templateID != null) {
                    output += `/tm_${templateID}`;
                    if (charGroupID != null) {
                        output += `/cg_${charGroupID}`;
                        if (charID != null) {
                            output += `/ch_${charID}`;
                        }
                    }
                }
            }
        }
        return output;
    }

    static getDataEntityAcls(charDataCollection: Models.ICharacteristicMetaDataCollection) {
        return _.transform(charDataCollection.characteristicMetaDatas,
            (acc, cmd) => {
                acc[cmd.characteristicID] = AclUtil.getDataEntityAcl(charDataCollection.charTypeID, charDataCollection.template.templateGroupID, charDataCollection.templateID, cmd.groupID, cmd.characteristicID);
            }, <_.Dictionary<string>>{});
    }

    static filterCMDsByRights(aclSet: string[], charDataCollection: Models.ICharacteristicMetaDataCollection, rightFlag: number) {
        return charDataCollection.characteristicMetaDatas.filter((cmd) => AclUtil.hasAccess(aclSet, AclUtil.getDataEntityAcl(charDataCollection.charTypeID, charDataCollection.template.templateGroupID, charDataCollection.templateID, cmd.groupID, cmd.characteristicID), rightFlag));
    }


    private static hash(str) {
        var hash = 5381,
            i = str.length;

        while (i) {
            hash = (hash * 33) ^ str.charCodeAt(--i);
        }

		/* JavaScript does bitwise operations (like XOR, above) on 32-bit signed
		 * integers. Since we want the results to be always positive, convert the
		 * signed int to an unsigned by doing an unsigned bitshift. */
        return hash >>> 0;
    }

    private static effectivePermissionCache: _.Dictionary<number> = {};
    static count = 1;
    static getEffectivePermissionValue(aclSet: string[], targetAcl: string) {
        var key = AclUtil.hash(aclSet.join("|")) + targetAcl;
        var output = AclUtil.effectivePermissionCache[key];
        if (output == null) {

            //console.log("calling getEffectivePermissionValue " + AclUtil.count++);
            var aclUris = aclSet.map((acl) => URI.parse(acl));
            var effective = 0;
            if (!_.isEmpty(targetAcl)) {
                var targetUri = URI.parse(targetAcl.toLowerCase());
                aclUris = aclUris.filter((acl) => acl.hostname == targetUri.hostname);
                var targetParts = targetUri.path.split("/");

                while (targetParts.length > 0) {
                    var matchPath = targetParts.join("/");
                    var matchUri = _.find(aclUris, (uri) => uri.path == matchPath);
                    if (matchUri != null) {
                        var rightFlag = _.parseInt(URI.parseQuery(matchUri.query)[Consts.Acl.ValueParam] || 0);
                        effective |= rightFlag;
                    }
                    targetParts.pop();
                }
            }
            output = effective;
            AclUtil.effectivePermissionCache[key] = output;
        }
        return output;
    }

    static getAccessResult(aclSet: string[], targetAcl: string, rightFlag: number = Consts.AccessRight.None, matchAll: boolean = true) {

        if (rightFlag == Consts.AccessRight.None) {
            var targetUri = URI.parse(targetAcl);
            rightFlag = _.parseInt(URI.parseQuery(targetUri.query)[Consts.Acl.ValueParam] || 0);
        }

        if (rightFlag != Consts.AccessRight.None) {
            var effectivePermissionValue = this.getEffectivePermissionValue(aclSet, targetAcl);
            return this.calcResult(effectivePermissionValue, rightFlag, matchAll);
        }
        return Consts.AccessResult.NotSet;
    }

    static calcResult(effectivePermissionValue: number, rightFlag: number, matchAll: boolean = true) {
        var denyFlag = rightFlag << 16;
        if ((effectivePermissionValue & denyFlag) > 0) {
            return Consts.AccessResult.Deny;
        } else if (!matchAll && ((effectivePermissionValue & rightFlag) > 0)) {
            return Consts.AccessResult.Allow;
        } else if (matchAll && ((effectivePermissionValue & rightFlag) == rightFlag)) {
            return Consts.AccessResult.Allow;
        } else {
            return Consts.AccessResult.NotSet;
        }
    }

    static hasAccess(aclSet: string[], targetAcl: string, rightFlag: number = Consts.AccessRight.None) {
        return this.getAccessResult(aclSet, targetAcl, rightFlag) == Consts.AccessResult.Allow;
    }

    static hasReadAccess(aclSet: string[], targetAcl: string) {
        return this.hasAccess(aclSet, targetAcl, Consts.AccessRight.Read);
    }

    static hasWriteAccess(aclSet: string[], targetAcl: string) {
        return this.hasAccess(aclSet, targetAcl, Consts.AccessRight.Write);
    }

    static hasCreateAccess(aclSet: string[], targetAcl: string) {
        return this.hasAccess(aclSet, targetAcl, Consts.AccessRight.Create);
    }

    static hasDeleteAccess(aclSet: string[], targetAcl: string) {
        return this.hasAccess(aclSet, targetAcl, Consts.AccessRight.Delete);
    }

    static hasAdminAccess(aclSet: string[], targetAcl: string) {
        return this.hasAccess(aclSet, targetAcl, Consts.AccessRight.Admin);
    }

    static withPermissionValue(acl: string, value: number) {
        if (value == null) {
            return URI(acl).removeQuery(Consts.Acl.ValueParam).valueOf();
        } else {
            return URI(acl).setQuery(Consts.Acl.ValueParam, value.toString()).valueOf();
        }
    }
}

/*
 * Class helps you build and parse EntityIDs <-> strings
 */

export class IdBuilder {

    static entityRegex: RegExp = /d(\d+)c(\d+)r(-?\d+)/;
    static templateRegex: RegExp = /d(\d+)c(\d+)t(\d+)/;


    private static newID: number = -100;

	/*
	 * Build EntityID string from components
	 * @param divID division ID
	 * @param charTypeID charType ID
	 * @param recordID record ID
	 * @returns string representation of EntityID
	 */
    static buildEntityID(divID: number, charTypeID: number, recordID: number) {
        return "d" + divID + "c" + charTypeID + "r" + recordID;
    }

    /* gets d0c0r0 relationship id from relationship object */
    static buildRelationshipID(relationship: Models.IEntityRelationship) {
        return this.buildEntityID(relationship.divisionID, Consts.CharTypeIDs.relationship, relationship.relRecID);
    }

	/*
	 * build a TemplateEntityID from components
	 * @returns a string represention of the TemplateID
	 */
    static buildTemplateID(divID: number, charTypeID: number, templateID: number) {
        return "d" + divID + "c" + charTypeID + "t" + templateID;
    }
	/*
	 * split a string into a typed EntityID
	 */
    static splitEntityID(entityID: string): Models.IEntityID {
        var output = { divID: -1, charTypeID: -1, recID: -1 };
        if (this.isEntityID(entityID)) {
            var match = this.entityRegex.exec(entityID);
            if (match.length == 4) {
                output.divID = _.parseInt(match[1]);
                output.charTypeID = _.parseInt(match[2]);
                output.recID = _.parseInt(match[3]);
            }
        }
        return output;
    }

	/*
	 * split a string into a typed TemplateID
	 */
    static splitTemplateID(entityTemplateID: string): Models.TemplateEntityID {
        var output = { divID: -1, charTypeID: -1, templateID: -1 };
        if (this.isTemplateID(entityTemplateID)) {
            var match = this.templateRegex.exec(entityTemplateID);
            if (match.length == 4) {
                output.divID = _.parseInt(match[1]);
                output.charTypeID = _.parseInt(match[2]);
                output.templateID = _.parseInt(match[3]);
            }
        }
        return output;
    }

	/*
	 * test if string is a templateID  d1c1t1
	 */
    static isTemplateID(id: string) {
        return this.templateRegex.test(id);
    }
	/*
	 * test if string is an EntityID d1c1r1
	 */
    static isEntityID(id: string) {
        return this.entityRegex.test(id);
    }

    static getNewTempRecID() {
        return --IdBuilder.newID;
    }
}

export class UrlBuilder {

    private static _configurationTemplateTemplateMetaData = "/Configuration/Template/TemplateMetaData/{templateID}";
    private static _configuartionCharacteristic = "/Configuration/Characteristic";

    static getCharTypeSummaryUrl(charTypeID: number): string {
        switch (charTypeID) {
            case Consts.CharTypeIDs.property: {
                return "/Asset/Details";
            }
            case Consts.CharTypeIDs.user: {
                return "/Contact/Details";
            }
            case Consts.CharTypeIDs.document: {
                return "/Documents/Details";
            }
            case Consts.CharTypeIDs.transaction: {
                return "/Contract/Summary";
            }
            case Consts.CharTypeIDs.invoice: {
                return "/Financial/Details";
            }
            case Consts.CharTypeIDs.inventory: {
                return "/Inventory/Details";
            }
            case Consts.CharTypeIDs.job: {
                return "/Jobs/Details";
            }
            case Consts.CharTypeIDs.project: {
                return "/Project/Details";
            }
            default: return undefined;
        }
    }

    static configurationTemplateTemplateMetaDataUrl(charTypeID: number, templateID: number) {
        return UrlBuilder.buildUrl(
            this._configurationTemplateTemplateMetaData,
            {
                templateID: templateID,
                q: {
                    charTypeID: charTypeID
                }
            });
    }

    static configurationCharacteristicsUrl(charTypeID: number) {
        return UrlBuilder.buildUrl(
            this._configuartionCharacteristic,
            {
                q: {
                    charTypeID: charTypeID
                }
            });
    }

    static buildUrl(url, data: {} = {}) {
        url = url + "{?q*}";
        return URI.expand(url, data).href();
    }

}

export class TreeUtil {
    static GetSelectionDescription(listOfValues: Models.ICharacteristicMetaDataValue[], charDatas: Models.ICharDataValue[]): { included: string[]; excluded: string[] } {

        var topLov = "";
        var excludedLovs: string[] = [];
        var paths: number[][] = [];
        var currentLovIDs = _.map(charDatas, (cd) => cd.valueID);

        var getPaths = (lov: Models.ICharacteristicMetaDataValue, currentPaths: number[]) => {
            var clonedPaths = _.clone(currentPaths);
            // push current lov to current path list
            clonedPaths.push(lov.characteristicValueID);
            if (_.contains(currentLovIDs, lov.characteristicValueID)) {
                paths.push(clonedPaths);
            } else if (lov.childValues.length > 0) {
                _.forEach(lov.childValues, (childLov) => { getPaths(childLov, clonedPaths); });
            }
        };

        var findLov = (lov: Models.ICharacteristicMetaDataValue, valueID: number): Models.ICharacteristicMetaDataValue => {
            if (lov.characteristicValueID == valueID) {
                return lov;
            }
            for (var i = 0; i < lov.childValues.length; i++) {
                var childLov = lov.childValues[i];
                var result = findLov(childLov, valueID);
                if (result != null) {
                    return result;
                }
            }
            return null;
        };

        var hasSelectedChildren = (lov: Models.ICharacteristicMetaDataValue) => {
            if (_.any(lov.childValues, (child) => _.contains(currentLovIDs, child.characteristicValueID))) {
                return true;
            }
            for (var i = 0; i < lov.childValues.length; i++) {
                var childLov = lov.childValues[i];
                var result = hasSelectedChildren(childLov);
                if (result) {
                    return true;
                }
            }
            return false;
        };

        var findUnSelectedLovs = (lov: Models.ICharacteristicMetaDataValue) => {
            // if the node is not part of the selected models
            if (!_.contains(currentLovIDs, lov.characteristicValueID)) {
                if (hasSelectedChildren(lov)) {
                    _.forEach(lov.childValues, (childLov) => { findUnSelectedLovs(childLov); });
                } else {
                    excludedLovs.push(lov.characteristicValueLabel);
                }
            }
        };

        _.forEach(listOfValues, (lov) => { getPaths(lov, []); });

        var intersectioned: number[] = _.intersection.apply(_, paths);

        var lowestCommonLovID: number = _.last(intersectioned);
        if (lowestCommonLovID != null) {
            var commonLov = _(listOfValues).map((lov: Models.ICharacteristicMetaDataValue) => { return findLov(lov, lowestCommonLovID); }).first();
            if (commonLov != null) {
                topLov = commonLov.characteristicValueLabel;
                _.forEach(commonLov.childValues, findUnSelectedLovs);
            }
        }

        if (excludedLovs.length > 0 && (excludedLovs.length < charDatas.length)) {
            return {
                included: [topLov],
                excluded: excludedLovs
            };
        } else {
            return {
                included: _.map(charDatas, (cd) => cd.value),
                excluded: []
            };
        }
    }

    static GetSelectedPaths(listOfValues: Models.ICharacteristicMetaDataValue[], valueIDs: number[]): string[] {

        var output = [];

        var walkLovs = (lovs: Models.ICharacteristicMetaDataValue[], currentPath: number[]) => {
            // for each lov
            lovs.forEach((lov) => {
                // append path
                var lovPath = currentPath.concat(lov.characteristicValueID);
                if (_.contains(valueIDs, lov.characteristicValueID)) {
                    // add string path to output
                    output.push(lovPath.join("/"));
                } else if (lov.childValues.length > 0) {
                    // recurse on children
                    walkLovs(lov.childValues, lovPath);
                }
            });
        };

        // run recusive on input, start with empty path array
        walkLovs(listOfValues, []);

        return output;
    }

    static GetExpandedIDs(lov: Models.ICharacteristicMetaDataValue[]): number[] {
        var output: number[] = [];
        var walkLovs = (lovs: Models.ICharacteristicMetaDataValue[]) => {
            lovs.forEach((lov2) => {
                output.push(lov2.characteristicValueID);
                if (lov2.childValues.length > 0) {
                    walkLovs(lov2.childValues);
                }
            });
        };
        return output;
    }

    static ClipText(text: string, length: number, ellipsis: boolean): string {

        text = text.replace("&nbsp;", " ").replace("  ", " ");

        if (text.length > length) {
            if (ellipsis) {
                length -= 3;
            }

            var num = text.lastIndexOf(" ", length);

            if (num < 0) {
                num = length;
            }

            text = text.substring(0, num);

            if (ellipsis) {
                text = text + "...";
            }
        }

        return text;
    }
}

export class DateUtil {

    static IsPerpetuity(date: moment.Moment): boolean {
        return date.isValid() && date.date() == 9 && date.month() + 1 == 9 && date.year() == 9999;
    }

    static IsISODate(input: string): boolean {
        return /^\s*(?:[+-]\d{6}|\d{4})-(?:(\d\d-\d\d)|(W\d\d$)|(W\d\d-\d)|(\d\d\d))((T| )(\d\d(:\d\d(:\d\d(\.\d+)?)?)?)?([\+\-]\d\d(?::?\d\d)?|\s*Z)?)?$/.test(input);
    }

    static CheckForCircularDateMathReferences(charDatas: Models.ICharDataValue[], contextRecCharID: number, parentRecCharID: number): boolean {
        var trail: number[] = [contextRecCharID];
        var recurse = (recCharID: number) => {
            trail.push(recCharID);
            var cd = _.find(charDatas, cd1 => cd1.recCharID == recCharID);
            if (cd != null && cd.ext != null && cd.ext.dateMath != null) {
                var rel = _.find(cd.ext.dateMath.relationships, rel => rel.recCharID == recCharID);
                if (rel != null) {
                    if (_.any(trail, v1 => v1 == rel.parentRecCharID)) {
                        return true;
                    } else {
                        return recurse(rel.parentRecCharID);
                    }
                }
            }
            return false;
        }
        return recurse(parentRecCharID);
    }

}

export class CharUtil {

    static GetCurrencyLovCmds(templateMetaData: Models.ICharacteristicMetaDataCollection) {
        if (templateMetaData == null) {
            return null;
        }
        return _.filter(templateMetaData.characteristicMetaDatas, (cmd) => cmd.systemIndicator == Consts.SystemIndicators.Currency);
    }

    static GetCurrencyLovCmd(templateMetaData: Models.ICharacteristicMetaDataCollection, groupID?: number) {
        if (templateMetaData == null) {
            return null;
        }
        var currencyCharMetaData = _(templateMetaData.characteristicMetaDatas).sortBy(cmd => cmd.groupID).find((cmd) => cmd.systemIndicator == Consts.SystemIndicators.Currency);
        if (groupID != null && currencyCharMetaData != null && currencyCharMetaData.groupID != groupID) {
            currencyCharMetaData = _.find(templateMetaData.characteristicMetaDatas, (cmd) => cmd.systemIndicator == Consts.SystemIndicators.Currency && cmd.groupID == groupID) || currencyCharMetaData;
        }
        return currencyCharMetaData;
    }

    static CopyRecordDataToEntityDocument(charDataCollection: Models.ICharDataCollection, document: Models.IEntitySearchDoc) {
        document.entityID = charDataCollection.entityID;

        const split = IdBuilder.splitEntityID(document.entityID);
        document.lastUpdated = charDataCollection.charDatas['last_updated'] ? charDataCollection.charDatas['last_updated'][0].value : undefined;
        document.charTypeID = split.charTypeID;
        document.divisionID = split.divID;
        document.recordID = split.recID;
        document.templateID = charDataCollection.templateMetaData.templateID;
        document.title = charDataCollection.charDatas['entity_title'] ? charDataCollection.charDatas['entity_title'][0].value : undefined;
        document.template = charDataCollection.templateMetaData.template.templateName;
        document.status = charDataCollection.status.stepName;
        document.statusID = charDataCollection.status.stepID;
    }

    static CopyMoneyFieldToEntityDocument(solrLabel: string, multipleIndicator: number, charDataValues: Models.ICharDataValue[], document: Models.IEntitySearchDoc) {

        if (multipleIndicator > 0) {
            const monies: Models.ICharDataMoneyValue[] = charDataValues.map((cdv) => {
                return JsonUtil.Parse(cdv.value, {});
            });
            document.characteristics[`${solrLabel}_sym_strs`] = monies.map((m) => { return m.locSym });
            document.characteristics[`${solrLabel}_strs`] = monies.map((m) => { return `${m.locSym} ${parseFloat(m.locAmt).toLocaleString()}` });
            document.characteristics[`${solrLabel}_doubles`] = monies.map((m) => { return `${parseFloat(m.locAmt)}` });
        } else {
            if (!_.isEmpty(charDataValues[0])) {
                const money: Models.ICharDataMoneyValue = JsonUtil.Parse(charDataValues[0].value, {});
                const locAmount = parseFloat(money.locAmt);
                document.characteristics[`${solrLabel}_sym_str`] = money.locSym;
                document.characteristics[`${solrLabel}_double`] = `${locAmount}`;
                document.characteristics[`${solrLabel}_str`] = `${money.locSym} ${locAmount.toLocaleString()}`;
                document.characteristics[`${solrLabel}_str_sort`] = `${money.locSym} ${locAmount.toLocaleString()}`;
            }
        }
    }

    static CopyCharDataToEntityDocument(charDatas: Models.ICharDataCollection, document: Models.IEntitySearchDoc) {
        if (!document.entityID) {
            this.CopyRecordDataToEntityDocument(charDatas, document);
        }

        charDatas.templateMetaData.characteristicMetaDatas.forEach((cmd) => {
            var solrLabel = this.GetCharSolrLabelByCmd(cmd);

            if (cmd.dataTypeID === Consts.CharDataTypeIDs.CharDataTypes.money) {
                this.CopyMoneyFieldToEntityDocument(cmd.tagLabel, cmd.multipleIndicator, charDatas.charDatas[cmd.tagLabel], document);
            }
            else {
                if (cmd.multipleIndicator == 0 && charDatas.charDatas[cmd.tagLabel] && charDatas.charDatas[cmd.tagLabel].length == 1) {
                    document.characteristics[solrLabel] = charDatas.charDatas[cmd.tagLabel][0].value;
                } else if (cmd.multipleIndicator == 1 && charDatas.charDatas[cmd.tagLabel]) {
                    if (charDatas.charDatas[cmd.tagLabel].length > 0) {
                        document.characteristics[solrLabel] = _.map(charDatas.charDatas[cmd.tagLabel], (val) => {
                            return val.value;
                        });
                    }
                    else {
                        document.characteristics[solrLabel] = [];
                    }
                } else if (cmd.multipleIndicator == 0 && charDatas.charDatas[cmd.tagLabel] && document.characteristics[solrLabel]) {
                    document.characteristics[solrLabel] = "";
                }
            }
        });

        return document;
    }

    static GetFormatter(dataTypeID: number) {
        switch (dataTypeID) {
            case Consts.CharDataTypeIDs.CharDataTypes.file: {
                return 'filename';
            }
            case Consts.CharDataTypeIDs.CharDataTypes.date: {
                return 'date:"L"';
            }
            default: {
                return undefined;
            }
        }
    }

    static GetCurrencyCharData(charDataCollection: Models.ICharDataCollection, groupID?: number) {
        if (charDataCollection == null) {
            return null;
        }
        var cmd = CharUtil.GetCurrencyLovCmd(charDataCollection.templateMetaData, groupID);
        if (cmd == null) {
            return null;
        }
        return _.first(charDataCollection.charDatas[cmd.tagLabel]);
    }

    static IsModuleLevelCharType(charTypeID: number) {

        var isModuleLevelCharType: boolean = false;

        switch (charTypeID) {
            case Consts.CharTypeIDs.user:
                isModuleLevelCharType = true;
                break;
            case Consts.CharTypeIDs.project:
                isModuleLevelCharType = true;
                break;
            case Consts.CharTypeIDs.property:
                isModuleLevelCharType = true;
                break;
            case Consts.CharTypeIDs.transaction:
                isModuleLevelCharType = true;
                break;
            case Consts.CharTypeIDs.inventory:
                isModuleLevelCharType = true;
                break;
            case Consts.CharTypeIDs.job:
                isModuleLevelCharType = true;
                break;
            case Consts.CharTypeIDs.invoice:
                isModuleLevelCharType = true;
                break;
                case Consts.CharTypeIDs.mock:
                isModuleLevelCharType = true;
                break;
            default:
                isModuleLevelCharType = false;
                break;
        }

        return isModuleLevelCharType;
    }

    static GetModuleLevelCharTyps(includeContacts: boolean = true): number[] {
        var moduleCharTypes: number[] = [];

        if (includeContacts) {
            moduleCharTypes.push(Consts.CharTypeIDs.user);
        }

        moduleCharTypes.push(Consts.CharTypeIDs.project);
        moduleCharTypes.push(Consts.CharTypeIDs.property);
        moduleCharTypes.push(Consts.CharTypeIDs.transaction);
        moduleCharTypes.push(Consts.CharTypeIDs.inventory);
        moduleCharTypes.push(Consts.CharTypeIDs.job);
        moduleCharTypes.push(Consts.CharTypeIDs.invoice);

        return moduleCharTypes;
    }

    static GetCharSolrLabel(dataTypeID: number, multipleIndicator: number, tagLabel: string) {
        if (tagLabel == "entity_status")
            return "status";
        else if (tagLabel == "entity_id")
            return "recordid";
        else if (tagLabel == "entity_title") {
            return "title";
        }
        else if (tagLabel == "entity_template") {
            return "template";
        }

        switch (dataTypeID) {
            case Consts.CharDataTypeIDs.CharDataTypes.email:
            case Consts.CharDataTypeIDs.CharDataTypes.timecode:
            case Consts.CharDataTypeIDs.CharDataTypes.password:
            case Consts.CharDataTypeIDs.CharDataTypes.url:
            case Consts.CharDataTypeIDs.CharDataTypes.money:
            case Consts.CharDataTypeIDs.CharDataTypes.alphanumeric:
            case Consts.CharDataTypeIDs.CharDataTypes.file:
            case Consts.CharDataTypeIDs.CharDataTypes.checkbox:
                return multipleIndicator > 0 ? `${tagLabel}_strs` : `${tagLabel}_str`;
            //case Consts.CharDataTypeIDs.CharDataTypes.checkbox:  //easier to use the LOV indexed properties of a checkbox
            //    return multipleIndicator > 0 ? `${tagLabel}_strs` : `${tagLabel}_bool`;
            case Consts.CharDataTypeIDs.CharDataTypes.date:
                return multipleIndicator > 0 ? `${tagLabel}_dates` : `${tagLabel}_date`;
            case Consts.CharDataTypeIDs.CharDataTypes.year:
                return multipleIndicator > 0 ? `${tagLabel}_ints` : `${tagLabel}_int`;
            case Consts.CharDataTypeIDs.CharDataTypes.numeric:
            case Consts.CharDataTypeIDs.CharDataTypes.percentage:
            case Consts.CharDataTypeIDs.CharDataTypes.currency:
                return multipleIndicator > 0 ? `${tagLabel}_doubles` : `${tagLabel}_double`;
        }
    }

    static GetCharSolrLabelByCmd(cmd: Models.ICharacteristicMetaData): string {
        return this.GetCharSolrLabel(cmd.dataTypeID, cmd.multipleIndicator, cmd.tagLabel);
    }

    static isCharDataEmpty(charDatas: Models.ICharDataValue[]) {
        return charDatas != null && _.all(charDatas, (cd) => _.isEmpty(cd.value) && _.isEmpty(cd.valueID));
    }

    static GetCurrencyLovValueId(charDataCollection: Models.ICharDataCollection) {
        var currencyLovValueId: number;
        switch (charDataCollection.templateMetaData.charTypeID) {
            case Consts.CharTypeIDs.amount:
                var amountHasMoneyFields: boolean = false;
                var amountCharMD = _.find(charDataCollection.templateMetaData.characteristicMetaDatas, chr => chr.systemIndicator == Consts.SystemIndicators.Amount.Amount);
                if (amountCharMD.dataTypeID == Consts.CharDataTypeIDs.CharDataTypes.money) {
                    amountHasMoneyFields = true;
                }
                if (amountHasMoneyFields) {
                    var amountMoneyCharDataValues = charDataCollection.charDatas[amountCharMD.tagLabel];
                    if (!_.isEmpty(amountMoneyCharDataValues)) {
                        var moneyValue: Models.ICharDataMoneyValue = JsonUtil.Parse(amountMoneyCharDataValues[0].value, {});
                        currencyLovValueId = moneyValue.locCur;
                    }
                }
                else {
                    var amountCurrencyChars = charDataCollection.charDatas[Consts.TagLabels.Currency];
                    if (!_.isEmpty(amountCurrencyChars)) {
                        currencyLovValueId = amountCurrencyChars[0].valueID;
                    }
                }
                break;
            case Consts.CharTypeIDs.invoice:
                var invoiceHasMoneyFields: boolean = false;

                var invoiceTotalCharMD = charDataCollection.templateMetaData.characteristicMetaDatas.find(cmd => cmd.tagLabel == Consts.TagLabels.InvoiceTotal);
                var invoiceBalanceCharMD = charDataCollection.templateMetaData.characteristicMetaDatas.find(cmd => cmd.tagLabel == Consts.TagLabels.InvoiceBalance);
                if (invoiceTotalCharMD.dataTypeID == Consts.CharDataTypeIDs.CharDataTypes.money || invoiceBalanceCharMD.dataTypeID == Consts.CharDataTypeIDs.CharDataTypes.money) {
                    invoiceHasMoneyFields = true;
                }

                if (invoiceHasMoneyFields) {
                    var invoiceTotalChars = charDataCollection.charDatas[Consts.TagLabels.InvoiceTotal];
                    var invoiceBalanceChars = charDataCollection.charDatas[Consts.TagLabels.InvoiceBalance];

                    if (!_.isEmpty(invoiceTotalChars)) {
                        var invoiceTotalCharMoneyValue: Models.ICharDataMoneyValue = JsonUtil.Parse(invoiceTotalChars[0].value, {});
                        if (!_.isEmpty(invoiceTotalCharMoneyValue)) {
                            currencyLovValueId = invoiceTotalCharMoneyValue.locCur;
                        }
                    }
                    else if (!_.isEmpty(invoiceBalanceChars)) {
                        var invoiceBalanceCharMoneyValue: Models.ICharDataMoneyValue = JsonUtil.Parse(invoiceBalanceChars[0].value, {});
                        if (!_.isEmpty(invoiceBalanceCharMoneyValue)) {
                            currencyLovValueId = invoiceBalanceCharMoneyValue.locCur;
                        }
                    }
                }
                else {
                    var invoiceCurrencyChars = charDataCollection.charDatas[Consts.TagLabels.Currency];
                    if (!_.isEmpty(invoiceCurrencyChars)) {
                        currencyLovValueId = invoiceCurrencyChars[0].valueID;
                    }
                }
                break;

            default:
        }
        return currencyLovValueId;
    }
}

export class CharTypeUtil {
    static GetCharTypeName(charTypeID: number): string {
        var charTypeName: string = "";
        switch (charTypeID) {
            case 1:
                charTypeName = "Catalog Item";
                break;
            case 2:
                charTypeName = "Contact";
                break;
            case 3:
                charTypeName = "Right";
                break;
            case 4:
                charTypeName = "Deal";
                break;
            case 5:
                charTypeName = "Table";
                break;
            case 7:
                charTypeName = "Accounting Document";
                break;
            case 10:
                charTypeName = "Amount";
                break;
            case 14:
                charTypeName = "Document";
                break;
            case 15:
                charTypeName = "Job";
                break;
            case 16:
                charTypeName = "Project";
                break;
            case 17:
                charTypeName = "Inventory";
                break;
        }
        return charTypeName;
    }
}

export class GuidUtil {
    private static s4() {
        return Math.floor((1 + Math.random()) * 0x10000)
            .toString(16)
            .substring(1);
    }

    static IsEmpty(guid: string) {
        return guid == "00000000-0000-0000-0000-000000000000";
    }

    static New() {
        return this.s4() + this.s4() + '-' + this.s4() + '-' + this.s4() + '-' +
            this.s4() + '-' + this.s4() + this.s4() + this.s4();
    }
}

export class JsonUtil {

    static Parse(value: string, defaultValue: any = null): any {
        try {
            var output = JSON.parse(value);
            return output || defaultValue;
        }
        catch (err) {
            return defaultValue;
        }
    }

    static Stringify(object: any) {
        return JSON.stringify(object);
    }
}

export class StringUtil {
    static escapeHtml(value: string) {
        var entityMap = {
                '&': '&amp;',
                '<': '&lt;',
                '>': '&gt;',
                '"': '&quot;',
                "'": '&#39;',
                '/': '&#x2F;',
                '`': '&#x60;',
                '=': '&#x3D;'
            };

        return String(value).replace(/[&<>"'`=\/]/g, (s) => {
            return entityMap[s];
        });
    }
}
